import { useState } from "react";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiResponse, IIssue } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const getIssueUUID = createUUID();
const updateIssueUUID = createUUID();
const deleteIssueUUID = createUUID();

export const useIssue = () => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const [issue, setIssue] = useState<IIssue | null>(null);

  const getIssue = (id: string): Promise<IApiResponse<IIssue>> => {
    stateManager.abort(getIssueUUID);

    return stateManager.run(async () => {
      const res = await taskService.issues.getById({ id });
      setIssue(res.data);
      return res;
    }, getIssueUUID);
  };

  const updateIssue = ({
    id,
    data,
  }: {
    id: string;
    data: Partial<IIssue>;
  }): Promise<IApiResponse<IIssue>> => {
    stateManager.abort(updateIssueUUID);
    return stateManager.run(async () => {
      const response = await taskService.issues.update({ id, data });
      if (response.status === HTTP_STATUSES.OK) {
        setIssue(response.data);
      }
      return response;
    }, updateIssueUUID);
  };

  const deleteIssue = ({
    issueId,
    modifiedOn,
  }: {
    issueId: string;
    modifiedOn: string;
  }): Promise<IApiResponse> => {
    stateManager.abort(deleteIssueUUID);
    return stateManager.run(async () => {
      return taskService.issues.delete({
        id: issueId,
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Issue deleted successfully.",
          },
        },
      });
    }, deleteIssueUUID);
  };

  return {
    isLoading,
    issue,
    getIssue,
    setIssue,
    updateIssue,
    deleteIssue,
  };
};
